// components/LogEntryForm.jsx
// Form modal for creating/editing log entries
import React, { useState, useEffect } from "react";
import { createLogEntry, updateLogEntry, addLogNote } from "../utils/adminLogs";

export default function LogEntryForm({ open, onClose, existingEntry = null, onSuccess }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState("bug");
  const [status, setStatus] = useState("current");
  const [priority, setPriority] = useState("medium");
  const [tags, setTags] = useState("");
  const [resolvedInVersion, setResolvedInVersion] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (existingEntry) {
      setTitle(existingEntry.title || "");
      setDescription(existingEntry.description || "");
      setType(existingEntry.type || "bug");
      setStatus(existingEntry.status || "current");
      setPriority(existingEntry.priority || "medium");
      setTags((existingEntry.tags || []).join(", "));
      setResolvedInVersion(existingEntry.resolvedInVersion || "");
    } else {
      // Reset form
      setTitle("");
      setDescription("");
      setType("bug");
      setStatus("current");
      setPriority("medium");
      setTags("");
      setResolvedInVersion("");
      setNote("");
    }
  }, [existingEntry, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      alert("Please enter a title");
      return;
    }

    setSaving(true);
    try {
      const tagsArray = tags
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t.length > 0);

      if (existingEntry) {
        // Update existing entry
        const updates = {
          title: title.trim(),
          description: description.trim(),
          status,
          priority,
          tags: tagsArray,
        };

        if (status === "resolved" || status === "completed") {
          updates.resolvedInVersion = resolvedInVersion.trim() || null;
        }

        await updateLogEntry(existingEntry.id, updates);

        // Add note if provided
        if (note.trim()) {
          await addLogNote(existingEntry.id, note.trim(), status !== existingEntry.status ? status : null);
        }

        onSuccess?.();
        onClose();
      } else {
        // Create new entry
        await createLogEntry({
          type,
          title: title.trim(),
          description: description.trim(),
          status: type === "bug" ? "current" : "planned",
          priority,
          tags: tagsArray,
          createdBy: "system",
        });

        onSuccess?.();
        onClose();
      }
    } catch (err) {
      console.error("Error saving log entry:", err);
      alert("Failed to save: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const bugStatuses = ["current", "investigating", "resolved"];
  const enhancementStatuses = ["planned", "in-progress", "completed"];

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 10000,
        backdropFilter: "blur(4px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "rgba(28,34,58,0.95)",
          borderRadius: 20,
          padding: "28px 32px",
          maxWidth: "90vw",
          width: "min(720px, 90vw)",
          maxHeight: "90vh",
          overflow: "auto",
          color: "#fff",
          border: "1px solid rgba(255,255,255,0.15)",
          boxShadow: "0 12px 48px rgba(0,0,0,0.5)",
        }}
      >
        <h2 style={{ marginTop: 0, marginBottom: 20, color: "#ffe066" }}>
          {existingEntry ? "Edit Entry" : "Create New Entry"}
        </h2>

        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gap: 16, marginBottom: 20 }}>
            {!existingEntry && (
              <div>
                <label style={{ display: "block", marginBottom: 6, fontWeight: 700 }}>
                  Type
                </label>
                <select
                  value={type}
                  onChange={(e) => {
                    setType(e.target.value);
                    setStatus(e.target.value === "bug" ? "current" : "planned");
                  }}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: 8,
                    border: "1px solid rgba(255,255,255,0.2)",
                    background: "rgba(0,0,0,0.3)",
                    color: "#fff",
                  }}
                >
                  <option value="bug">Bug</option>
                  <option value="enhancement">Enhancement</option>
                </select>
              </div>
            )}

            <div>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 700 }}>
                Title *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1px solid rgba(255,255,255,0.2)",
                  background: "rgba(0,0,0,0.3)",
                  color: "#fff",
                  fontSize: "1em",
                }}
              />
            </div>

            <div>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 700 }}>
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={6}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1px solid rgba(255,255,255,0.2)",
                  background: "rgba(0,0,0,0.3)",
                  color: "#fff",
                  fontSize: "1em",
                  fontFamily: "inherit",
                  resize: "vertical",
                }}
              />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <label style={{ display: "block", marginBottom: 6, fontWeight: 700 }}>
                  Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: 8,
                    border: "1px solid rgba(255,255,255,0.2)",
                    background: "rgba(0,0,0,0.3)",
                    color: "#fff",
                  }}
                >
                  {(existingEntry?.type === "bug" || type === "bug"
                    ? bugStatuses
                    : enhancementStatuses
                  ).map((s) => (
                    <option key={s} value={s}>
                      {s.charAt(0).toUpperCase() + s.slice(1).replace("-", " ")}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 6, fontWeight: 700 }}>
                  Priority
                </label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: 8,
                    border: "1px solid rgba(255,255,255,0.2)",
                    background: "rgba(0,0,0,0.3)",
                    color: "#fff",
                  }}
                >
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>

            <div>
              <label style={{ display: "block", marginBottom: 6, fontWeight: 700 }}>
                Tags (comma-separated)
              </label>
              <input
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="e.g., crash, player, mobile"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1px solid rgba(255,255,255,0.2)",
                  background: "rgba(0,0,0,0.3)",
                  color: "#fff",
                  fontSize: "1em",
                }}
              />
            </div>

            {(status === "resolved" || status === "completed") && (
              <div>
                <label style={{ display: "block", marginBottom: 6, fontWeight: 700 }}>
                  Resolved in Version (optional)
                </label>
                <input
                  type="text"
                  value={resolvedInVersion}
                  onChange={(e) => setResolvedInVersion(e.target.value)}
                  placeholder="e.g., v2.1.0"
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid rgba(255,255,255,0.2)",
                    background: "rgba(0,0,0,0.3)",
                    color: "#fff",
                    fontSize: "1em",
                  }}
                />
              </div>
            )}

            {existingEntry && (
              <div>
                <label style={{ display: "block", marginBottom: 6, fontWeight: 700 }}>
                  Add Note (optional)
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  placeholder="Add a note about this change..."
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid rgba(255,255,255,0.2)",
                    background: "rgba(0,0,0,0.3)",
                    color: "#fff",
                    fontSize: "1em",
                    fontFamily: "inherit",
                    resize: "vertical",
                  }}
                />
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              style={{
                padding: "10px 20px",
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.2)",
                background: "rgba(255,255,255,0.1)",
                color: "#fff",
                fontWeight: 700,
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.5 : 1,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: "10px 20px",
                borderRadius: 10,
                border: "none",
                background: "#47e373",
                color: "#111",
                fontWeight: 900,
                cursor: saving ? "wait" : "pointer",
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? "Saving..." : existingEntry ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
